import apiService from '../Api'
import {
  IAdmissionPageRaw,
  IEmailCheckRaw,
  IEmployeeDetailRaw,
  IEmployeeDocumentRaw,
  IEmployeeFilters,
  IEmployeeOptionRaw,
  IEmployeePageRaw,
  IEvaluationEmployeeRaw,
  TAdmissionStage,
  TEmployeeStatus
} from './types'

/** Admissions kanban and collaborators (ABRA-320). */
export class EmployeeService {
  async admissions(
    stage: TAdmissionStage,
    filters: IEmployeeFilters,
    offset: number,
    limit: number
  ): Promise<IAdmissionPageRaw> {
    return apiService
      .get('/admissions', { params: { stage, ...this.filterParams(filters), offset, limit } })
      .then((response) => response.data)
  }

  async list(
    status: TEmployeeStatus,
    filters: IEmployeeFilters,
    page: number,
    perPage: number
  ): Promise<IEmployeePageRaw> {
    return apiService
      .get('/employees', { params: { status, ...this.filterParams(filters), page, per_page: perPage } })
      .then((response) => response.data)
  }

  /** Active collaborators of one sector, for public forms (no login needed). */
  async listForForms(sectorId: number): Promise<IEmployeeOptionRaw[]> {
    return apiService.get('/employees/select', { params: { sector_id: sectorId } }).then((response) => response.data)
  }

  /** Active collaborators of one sector for the Magenta forms, e-mail and level included. */
  async listForEvaluations(sectorId: number): Promise<IEvaluationEmployeeRaw[]> {
    return apiService
      .get('/employees/evaluation-select', { params: { sector_id: sectorId } })
      .then((response) => response.data)
  }

  async show(id: number): Promise<IEmployeeDetailRaw> {
    return apiService.get(`/employees/${id}`).then((response) => response.data)
  }

  async emailCheck(email: string): Promise<IEmailCheckRaw> {
    return apiService.get('/employees/email-check', { params: { email } }).then((response) => response.data)
  }

  /** Usernames from the name; `exceptId` keeps the edited collaborator's own one available. */
  async usernames(name: string, exceptId?: number): Promise<string[]> {
    return apiService
      .get('/employees/usernames', { params: { name, except: exceptId } })
      .then((response) => response.data.usernames)
  }

  async create(data: FormData): Promise<IEmployeeDetailRaw> {
    return apiService.post('/employees', data).then((response) => response.data)
  }

  async update(id: number, data: FormData): Promise<IEmployeeDetailRaw> {
    data.append('_method', 'PUT')
    return apiService.post(`/employees/${id}`, data).then((response) => response.data)
  }

  /** `legacyIds`: the legacy records RH chose to link (same e-mail). */
  async approve(id: number, admissionDate: string, legacyIds: number[]): Promise<IEmployeeDetailRaw> {
    return apiService
      .post(`/employees/${id}/approve`, { admission_date: admissionDate, link_legacy_ids: legacyIds })
      .then((response) => response.data)
  }

  /** Reprovar: the admission is deleted for good, with its files. */
  async reject(id: number): Promise<void> {
    return apiService.delete(`/employees/${id}`).then(() => undefined)
  }

  async inactivate(id: number, terminationDate: string): Promise<IEmployeeDetailRaw> {
    return apiService
      .post(`/employees/${id}/inactivate`, { termination_date: terminationDate })
      .then((response) => response.data)
  }

  async reactivate(id: number, admissionDate: string): Promise<IEmployeeDetailRaw> {
    return apiService
      .post(`/employees/${id}/reactivate`, { admission_date: admissionDate })
      .then((response) => response.data)
  }

  /** A document of the role (by its requirement) or a loose one (by name). */
  async uploadDocument(
    id: number,
    file: File,
    target: { requirementId: number } | { name: string }
  ): Promise<IEmployeeDocumentRaw> {
    const data = new FormData()
    data.append('file', file)
    if ('requirementId' in target) data.append('job_position_document_id', String(target.requirementId))
    else data.append('name', target.name)

    return apiService.post(`/employees/${id}/documents`, data).then((response) => response.data)
  }

  async deleteDocument(id: number, documentId: number): Promise<void> {
    return apiService.delete(`/employees/${id}/documents/${documentId}`).then(() => undefined)
  }

  private filterParams(filters: IEmployeeFilters) {
    return {
      search: filters.search?.trim() || undefined,
      sector_id: filters.sector_id || undefined,
      job_position_id: filters.job_position_id || undefined
    }
  }
}
